import type { KeyValuePair } from '@invision/core';
import { SessionHelpers, transformKeyValueArrayToObject } from '@invision/core';
import type { Security_Preference, Security_SessionSummary } from 'invApi';
import { AUTHENTICATION_ROUTES } from '../constants/routes.constants';
import { hasExternalReferenceInUrl } from './externalReferenceHelpers';

/**
 * Resume ("last URL") support for post-authentication redirects.
 *
 * The page a user should return to after re-authenticating is stored as a
 * server-side user preference keyed by the active business unit (written by the
 * shared idle-timeout handler). Reading it from the authenticated user's own
 * session — rather than from tab-scoped `sessionStorage` — guarantees a user is
 * never resumed onto a page that belonged to a different user on a shared,
 * reused browser tab.
 */

/**
 * Read the stored resume URL for the active business unit from a session
 * summary, or `null` when none is set.
 *
 * @param sessionSummary - Session summary returned by create/restore/retrieve
 * @param systemId - The active business unit's system id
 */
export function getResumeUrlFromSession(
  sessionSummary: Security_SessionSummary | undefined,
  systemId: string | null
): string | null {
  if (!sessionSummary || !systemId) {
    return null;
  }

  const preferences = transformKeyValueArrayToObject(
    sessionSummary.Preferences
  );

  return (
    preferences[SessionHelpers.getUserLastUrlPreferenceKey(systemId)] || null
  );
}

/**
 * Resolve the URL to feed into the post-authentication redirect.
 *
 * Precedence:
 * 1. The server-side resume URL (per-user, per-business-unit) is authoritative.
 * 2. Otherwise the tab-scoped stored redirect is honored ONLY when it carries
 *    external-reference (deep-link) parameters. A plain stored redirect is
 *    deliberately ignored: on a shared, reused tab it may belong to a previous
 *    user and would leak their page to the current user.
 *
 * @param resumeUrl - Server-side resume URL for the active business unit
 * @param storedRedirectUrl - Tab-scoped `sessionStorage` redirect URL
 */
export function resolvePostAuthRedirectUrl(
  resumeUrl: string | null,
  storedRedirectUrl: string | null
): string | null {
  if (resumeUrl) {
    return resumeUrl;
  }

  if (!storedRedirectUrl) {
    return null;
  }

  const queryIndex = storedRedirectUrl.indexOf('?');
  const search = queryIndex >= 0 ? storedRedirectUrl.slice(queryIndex) : '';

  return hasExternalReferenceInUrl(search) ? storedRedirectUrl : null;
}

/**
 * Path prefix of the React authentication app and the top-level route segments
 * it owns. A resume URL that points at any of these is never persisted: sending
 * a user back to a sign-in/logout page after they re-authenticate would be
 * pointless (and could loop), so these are treated as non-resumable.
 */
const AUTH_APP_PATH_PREFIX = '/authentication';

/**
 * The first path segment of a relative path, without leading slashes and
 * lower-cased (e.g. `/Login` → `login`). Any query/hash should be stripped by
 * the caller first.
 */
function getFirstPathSegment(path: string): string {
  return path.replace(/^\/+/, '').split('/')[0].toLowerCase();
}

/**
 * Top-level auth route segments the React auth app owns, derived from
 * `AUTHENTICATION_ROUTES` (the single source of truth) so the guard stays in
 * sync as routes are added or renamed.
 */
const AUTH_APP_ROUTE_SEGMENTS = [
  AUTHENTICATION_ROUTES.LOGIN,
  AUTHENTICATION_ROUTES.LOGOUT,
  AUTHENTICATION_ROUTES.FORGOT_PASSWORD,
  AUTHENTICATION_ROUTES.UPDATE_CREDENTIALS,
  AUTHENTICATION_ROUTES.IDP_CALLBACK_BASE,
  AUTHENTICATION_ROUTES.ACCESS_REDIRECT,
  AUTHENTICATION_ROUTES.NO_MODULE_ACCESS
].map(getFirstPathSegment);

/**
 * Whether a relative URL points at the authentication app (either served under
 * `/authentication` or one of its top-level auth routes). Used to reject
 * auth/logout URLs as "resume last URL" values.
 *
 * @param url - Relative URL (path plus optional query/hash)
 */
export function isAuthAppUrl(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];

  if (
    path === AUTH_APP_PATH_PREFIX ||
    path.startsWith(`${AUTH_APP_PATH_PREFIX}/`)
  ) {
    return true;
  }

  return AUTH_APP_ROUTE_SEGMENTS.includes(getFirstPathSegment(path));
}

/**
 * Whether a URL is safe to persist as the "resume last URL" preference.
 *
 * Only same-origin relative paths that do not point at the authentication app
 * qualify. Protocol-relative (`//host`) and absolute URLs are rejected so a
 * crafted `lastUrl` can never redirect the user off-origin after login.
 *
 * @param url - Candidate resume URL (typically the `lastUrl` query param)
 */
export function isSavableResumeUrl(
  url: string | null | undefined
): url is string {
  if (!url) {
    return false;
  }

  const trimmed = url.trim();

  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return false;
  }

  return !isAuthAppUrl(trimmed);
}

/**
 * Build the full preference set with the active business unit's resume URL
 * removed, for sending to `Security/UpdatePreferences` after the resume
 * location has been consumed. The whole set is returned so unrelated
 * preferences are preserved.
 *
 * @param sessionSummary - Session summary the resume URL was read from
 * @param systemId - The active business unit's system id
 */
export function buildClearedResumePreferences(
  sessionSummary: Security_SessionSummary | undefined,
  systemId: string
): KeyValuePair<string>[] {
  const preferences = transformKeyValueArrayToObject(
    sessionSummary?.Preferences as Security_Preference[] | undefined
  );

  return SessionHelpers.buildPreferencesWithoutLastUrl(preferences, systemId);
}
